import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/hooks/useSettings";
import { SEO } from "@/components/SEO";
import { Logo } from "@/components/Header";
import { GoogleIcon, startGoogleLogin } from "@/components/GooglePrompt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function AuthShell({ title, subtitle, children, footer, testId }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-10" data-testid={testId}>
      <SEO title={title} noindex />
      <Logo className="mb-8" />
      <div className="card w-full max-w-md animate-rise p-6 sm:p-8">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
      {footer && <p className="mt-6 text-sm text-slate-500">{footer}</p>}
    </div>
  );
}

function Field({ id, label, ...props }) {
  return (
    <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} className="h-11 rounded-xl" {...props} /></div>
  );
}

function GoogleButton() {
  return (
    <>
      <button type="button" onClick={startGoogleLogin} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50" data-testid="google-login-btn"><GoogleIcon /> Continue with Google</button>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />or<span className="h-px flex-1 bg-slate-200" /></div>
    </>
  );
}

export function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      navigate(location.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Welcome back" subtitle="Log in to manage your files." testId="login-page" footer={<>No account? <Link to="/register" className="font-medium text-indigo-600 hover:underline" data-testid="login-register-link">Create one</Link></>}>
      <GoogleButton />
      <form onSubmit={submit} className="space-y-4">
        <Field id="email" label="Email" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" />
        <Field id="password" label="Password" type="password" autoComplete="current-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" />
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={busy} className="h-11 w-full rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="login-submit">{busy ? "Logging in…" : "Log in"}</Button>
        <p className="text-center text-sm"><Link to="/forgot-password" className="text-slate-500 hover:text-indigo-600" data-testid="forgot-password-link">Forgot your password?</Link></p>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { setUser } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Welcome to Fileuploady!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Create your free account" subtitle="50 GB of storage and files that never expire." testId="register-page" footer={<>Already registered? <Link to="/login" className="font-medium text-indigo-600 hover:underline" data-testid="register-login-link">Log in</Link></>}>
      {!settings.registration_enabled ? (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="registration-disabled">Registration is currently disabled.</p>
      ) : (
        <>
          <GoogleButton />
          <form onSubmit={submit} className="space-y-4">
            <Field id="name" label="Name" required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" />
            <Field id="email" label="Email" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
            <Field id="password" label="Password" type="password" autoComplete="new-password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
            <p className="text-xs text-slate-400">At least 8 characters with letters and numbers.</p>
            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="register-submit">{busy ? "Creating account…" : "Create account"}</Button>
            <p className="text-center text-xs text-slate-400">By registering you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
          </form>
        </>
      )}
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Reset your password" subtitle="Enter your email and we'll send you a reset link." testId="forgot-page" footer={<Link to="/login" className="text-indigo-600 hover:underline" data-testid="forgot-back-link">Back to login</Link>}>
      {result ? (
        <div className="space-y-3 text-sm" data-testid="forgot-result">
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-emerald-800">{result.message}</p>
          {result.dev_reset_url && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold">Email is not configured on this server.</p>
              <p className="mt-1">Development reset link:</p>
              <a href={result.dev_reset_url} className="mt-1 block break-all font-mono text-indigo-700 underline" data-testid="dev-reset-link">{result.dev_reset_url}</a>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="email" label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          <Button type="submit" disabled={busy} className="h-11 w-full rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="forgot-submit">{busy ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const token = params.get("token") || "";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message);
      navigate("/login", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Choose a new password" testId="reset-page">
      {!token ? <p className="text-sm text-red-600" data-testid="reset-missing-token">This reset link is invalid.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="password" label="New password" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="reset-error">{error}</p>}
          <Button type="submit" disabled={busy} className="h-11 w-full rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="reset-submit">{busy ? "Saving…" : "Set new password"}</Button>
        </form>
      )}
    </AuthShell>
  );
}
